import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { subscriptionApi } from "@/lib/api/subscriptionapi";
import { getEffectiveLimits, type EffectiveLimits } from "@/lib/subscriptionLimits";
import { authSlice } from "./authSlice";
import type { RootState } from "./store";

/**
 * Convert -1 sentinel values back to Infinity.
 * The server sends -1 because JSON.stringify(Infinity) produces null.
 */
function fromJsonSafe(val: number | null | undefined): number {
  if (val === -1 || val === null || val === undefined) return Infinity;
  return val;
}

function hydrateFromApi(obj: any): any {
  if (!obj || typeof obj !== "object") return obj;
  const result: any = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (typeof val === "number") {
      result[key] = fromJsonSafe(val);
    } else if (Array.isArray(val)) {
      result[key] = val;
    } else if (typeof val === "object" && val !== null) {
      result[key] = hydrateFromApi(val);
    } else {
      result[key] = val;
    }
  }
  return result;
}

// ─── Types ────────────────────────────────────────────────────────

export interface SubscriptionUsage {
  activeProjects: number;
  maxActiveProjects: number;
  aiReportsUsed: number;
  aiReportsLimit: number;
  aiReportsRemaining: number;
  activeUsers: number;
  userCap: number;
  allowedRoles: string[];
  transactionHistoryDays: number;
  apiAccess: boolean;
}

export interface ForcedPrivateProject {
  id: number;
  title: string;
  status: string;
  serverstartdate: string;
}

export interface SubscriptionState {
  /** Whether the initial fetch has completed */
  loaded: boolean;
  /** Whether a fetch is in progress */
  loading: boolean;
  /** Fetch error message, if any */
  error: string | null;

  /** Current plan name: "starter" | "standard" | "premium" */
  planName: string;
  /** Price amount in pence (only relevant for standard) */
  priceAmount: number;
  /** Billing interval */
  billingInterval: string | null;
  /** Subscription status */
  status: string;

  /** Derived effective limits for the current plan */
  limits: EffectiveLimits | null;
  /** Live usage counts */
  usage: SubscriptionUsage | null;

  /** Projects forced to private during a downgrade */
  forcedPrivateProjects: ForcedPrivateProject[];
  /** Whether the charity needs to re-select which projects stay active */
  requiresProjectSelection: boolean;
  /** Whether the current user is the charity head */
  isCharityHead: boolean;
  /** Whether any subscription plans (global or charity-specific) are configured */
  plansConfigured: boolean;
}

const initialState: SubscriptionState = {
  loaded: false,
  loading: false,
  error: null,
  planName: "starter",
  priceAmount: 0,
  billingInterval: null,
  status: "active",
  limits: null,
  usage: null,
  forcedPrivateProjects: [],
  requiresProjectSelection: false,
  isCharityHead: false,
  plansConfigured: false,
};

// ─── Thunks ───────────────────────────────────────────────────────

/**
 * Fetch the full subscription state from the server.
 * The enriched GET /subscription endpoint returns plan, limits, usage,
 * forced-private projects, project-selection flag, and isCharityHead.
 */
export const fetchSubscription = createAsyncThunk(
  "subscription/fetch",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;
    if (!token) return rejectWithValue("No auth token");

    try {
      const data = await subscriptionApi.getSubscription(token);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to fetch subscription");
    }
  },
);

/**
 * Submit a manual project selection after a downgrade.
 */
export const submitProjectSelection = createAsyncThunk(
  "subscription/submitProjectSelection",
  async (selectedProjectIds: number[], { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;
    if (!token) return rejectWithValue("No auth token");

    try {
      const data = await subscriptionApi.submitProjectSelection(selectedProjectIds, token);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to submit project selection");
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    /** Reset subscription state (e.g. on logout) */
    resetSubscription: () => initialState,
  },
  extraReducers: (builder) => {
    // ── fetchSubscription ──
    builder.addCase(fetchSubscription.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSubscription.fulfilled, (state, action) => {
      const data = action.payload;
      state.loading = false;
      state.loaded = true;
      state.error = null;

      // Subscription info
      const sub = data.subscription;
      state.planName = sub?.plan_name || "starter";
      state.priceAmount = sub?.price_amount || 0;
      state.billingInterval = sub?.billing_interval || null;
      state.status = sub?.status || "active";

      // Limits & usage from enriched API response
      // Convert -1 sentinel values back to Infinity (JSON can't serialize Infinity)
      state.limits = data.limits
        ? hydrateFromApi(data.limits)
        : getEffectiveLimits(state.planName, state.priceAmount, state.billingInterval || "monthly");
      state.usage = data.usage ? hydrateFromApi(data.usage) : null;

      // Downgrade info
      state.forcedPrivateProjects = data.forcedPrivateProjects || [];
      state.requiresProjectSelection = data.requiresProjectSelection || false;

      // Permissions
      state.isCharityHead = data.isCharityHead || false;

      // Plans configured (global or charity-specific)
      state.plansConfigured = data.plansConfigured || false;
    });
    builder.addCase(fetchSubscription.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || "Failed to fetch subscription";
      // Still mark as loaded so components don't stay in loading state forever
      state.loaded = true;
    });

    // ── submitProjectSelection ──
    builder.addCase(submitProjectSelection.fulfilled, (state) => {
      state.requiresProjectSelection = false;
      state.forcedPrivateProjects = [];
    });

    // ── Reset on logout ──
    builder.addCase(authSlice.actions.logout, () => initialState);
  },
});

export const { resetSubscription } = subscriptionSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────

export const selectSubscriptionLoaded = (state: RootState) => state.subscription.loaded;
export const selectSubscriptionLoading = (state: RootState) => state.subscription.loading;
export const selectPlanName = (state: RootState) => state.subscription.planName;
export const selectLimits = (state: RootState) => state.subscription.limits;
export const selectUsage = (state: RootState) => state.subscription.usage;
export const selectIsCharityHead = (state: RootState) => state.subscription.isCharityHead;
export const selectRequiresProjectSelection = (state: RootState) => state.subscription.requiresProjectSelection;
export const selectForcedPrivateProjects = (state: RootState) => state.subscription.forcedPrivateProjects;
export const selectPlansConfigured = (state: RootState) => state.subscription.plansConfigured;

export default subscriptionSlice.reducer;
